import {
  bodyForRegion,
  lightColourForRegion,
  topmarkColourForRegion,
  type BodyColour,
  type BodyShape,
  type ColourPattern,
  type IalaMark,
  type Region,
  type Topmark,
} from "@nav-games/iala";

interface BuoyFigureProps {
  mark: IalaMark;
  region: Region;
  size?: number;
  /// When true, draw a small flashing dot at the top of the buoy
  /// representing the light. Hidden in night-view variants the
  /// renderer hasn't been asked for (this app only needs daytime
  /// for now).
  showLight?: boolean;
  /// When true, the SVG fills its container (width/height 100%)
  /// instead of using a fixed pixel `size` — for dropping the buoy
  /// into a flexible grid cell.
  fill?: boolean;
}

const HEX: Record<BodyColour, string> = {
  red: "#c0382b",
  green: "#3aa055",
  yellow: "#f0c020",
  black: "#1c2530",
  white: "#f5f1e6",
  /// Maritime "Berlin blue" — IALA's chosen blue for the emergency
  /// wreck-marking buoy. Distinct from the body-stripe black on the
  /// E/W cardinals, but still mid-tone enough to read against white.
  blue: "#1f6fa5",
};

/// Renders an IALA buoy on a 100×100 viewbox. The buoy is laid out
/// over three vertical zones:
///   - top (y 8–28):  topmark
///   - middle (28–82): body, drawn per BodyShape + ColourPattern
///   - bottom (82–96): waterline tick + reflection bar
/// Region inversion is applied via the package's helpers so the
/// renderer doesn't have to know about regional rules.
export function BuoyFigure({
  mark,
  region,
  size = 100,
  showLight = false,
  fill = false,
}: BuoyFigureProps) {
  const body = bodyForRegion(mark, region);
  const topmarkColour = topmarkColourForRegion(mark, region);
  const lightColour = lightColourForRegion(mark, region);

  return (
    <svg
      viewBox="0 0 100 100"
      width={fill ? "100%" : size}
      height={fill ? "100%" : size}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: "block" }}
      aria-label={`${mark.name} (Region ${region})`}
    >
      {/* sea — pale */}
      <rect x="0" y="86" width="100" height="14" fill="#b6c7d9" />
      <Topmark shape={mark.topmark} colour={topmarkColour} />
      <Body shape={mark.bodyShape} pattern={body} />
      {/* waterline reflection */}
      <line x1="22" y1="86" x2="78" y2="86" stroke="#7d8b9a" strokeWidth="1" />
      {showLight && (
        <circle cx="50" cy="9" r="3.5" fill={HEX[lightColour]} stroke="#000" strokeWidth="0.8" />
      )}
    </svg>
  );
}

function Topmark({
  shape,
  colour,
}: {
  shape: Topmark;
  colour: BodyColour;
}) {
  const fill = HEX[colour];
  switch (shape) {
    case "none":
      return null;
    case "can":
      // Flat-topped cylinder — matches the port-hand body silhouette
      // exactly so the buoy reads as "red can with red can topmark".
      return (
        <rect
          x={42}
          y={14}
          width={16}
          height={14}
          fill={fill}
          stroke="#000"
          strokeWidth="0.8"
        />
      );
    case "cone-up":
      return <polygon points="50,12 60,28 40,28" fill={fill} />;
    case "cone-down":
      return <polygon points="40,12 60,12 50,28" fill={fill} />;
    case "sphere":
      return <circle cx="50" cy="20" r="8" fill={fill} />;
    case "2-spheres":
      return (
        <>
          <circle cx="50" cy="14" r="5" fill={fill} />
          <circle cx="50" cy="26" r="5" fill={fill} />
        </>
      );
    case "2-cones-points-up":
      return (
        <>
          <polygon points="50,8 58,20 42,20" fill={fill} />
          <polygon points="50,20 58,32 42,32" fill={fill} />
        </>
      );
    case "2-cones-points-down":
      return (
        <>
          <polygon points="42,8 58,8 50,20" fill={fill} />
          <polygon points="42,20 58,20 50,32" fill={fill} />
        </>
      );
    case "2-cones-base-to-base":
      // East: apexes outward → top points up, bottom points down
      return (
        <>
          <polygon points="50,8 58,20 42,20" fill={fill} />
          <polygon points="42,20 58,20 50,32" fill={fill} />
        </>
      );
    case "2-cones-points-together":
      // West: apexes inward → top points down, bottom points up
      return (
        <>
          <polygon points="42,8 58,8 50,20" fill={fill} />
          <polygon points="50,20 58,32 42,32" fill={fill} />
        </>
      );
    case "x":
      return (
        <g stroke={fill} strokeWidth="4" strokeLinecap="round">
          <line x1="42" y1="12" x2="58" y2="28" />
          <line x1="58" y1="12" x2="42" y2="28" />
        </g>
      );
    case "cross":
      return (
        <g stroke={fill} strokeWidth="4" strokeLinecap="round">
          <line x1="50" y1="10" x2="50" y2="30" />
          <line x1="40" y1="20" x2="60" y2="20" />
        </g>
      );
  }
}

function Body({
  shape,
  pattern,
}: {
  shape: BodyShape;
  pattern: ColourPattern;
}) {
  // Body bounds (rectangle the body fills before being clipped to
  // shape) — y 32 to 82, centred horizontally.
  const top = 32;
  const bottom = 82;
  const height = bottom - top;
  const left = 28;
  const right = 72;
  const width = right - left;
  const clipId = `buoy-clip-${shape}-${pattern.bands.join("-")}`;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          {shape === "can" && (
            <rect x={left} y={top} width={width} height={height} />
          )}
          {shape === "cone" && (
            <polygon
              points={`${50},${top} ${right},${bottom} ${left},${bottom}`}
            />
          )}
          {shape === "spar" && (
            <rect x={46} y={top} width={8} height={height} />
          )}
          {shape === "pillar" && (
            <rect x={left + 4} y={top} width={width - 8} height={height} />
          )}
          {shape === "sphere" && (
            <circle cx={50} cy={(top + bottom) / 2} r={height / 2} />
          )}
        </clipPath>
      </defs>

      {/* Outline behind clip so it survives — drawn first */}
      {shape === "can" && (
        <rect
          x={left}
          y={top}
          width={width}
          height={height}
          fill="none"
          stroke="#000"
          strokeWidth="1.5"
        />
      )}
      {shape === "cone" && (
        <polygon
          points={`${50},${top} ${right},${bottom} ${left},${bottom}`}
          fill="none"
          stroke="#000"
          strokeWidth="1.5"
        />
      )}
      {shape === "spar" && (
        <rect
          x={46}
          y={top}
          width={8}
          height={height}
          fill="none"
          stroke="#000"
          strokeWidth="1.5"
        />
      )}
      {shape === "pillar" && (
        <rect
          x={left + 4}
          y={top}
          width={width - 8}
          height={height}
          fill="none"
          stroke="#000"
          strokeWidth="1.5"
        />
      )}
      {shape === "sphere" && (
        <circle
          cx={50}
          cy={(top + bottom) / 2}
          r={height / 2}
          fill="none"
          stroke="#000"
          strokeWidth="1.5"
        />
      )}

      {/* Coloured bands clipped to body silhouette */}
      <g clipPath={`url(#${clipId})`}>
        {pattern.layout === "solid" && (
          <rect
            x={left - 4}
            y={top}
            width={width + 8}
            height={height}
            fill={HEX[pattern.bands[0]!]}
          />
        )}
        {pattern.layout === "horizontal" &&
          pattern.bands.map((c, i) => {
            const bandH = height / pattern.bands.length;
            return (
              <rect
                key={i}
                x={left - 4}
                y={top + i * bandH}
                width={width + 8}
                height={bandH}
                fill={HEX[c]}
              />
            );
          })}
        {pattern.layout === "vertical" &&
          pattern.bands.map((c, i) => {
            const bandW = (width + 8) / pattern.bands.length;
            return (
              <rect
                key={i}
                x={left - 4 + i * bandW}
                y={top}
                width={bandW}
                height={height}
                fill={HEX[c]}
              />
            );
          })}
      </g>
    </g>
  );
}
